import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Smartphone, MessageCircle } from 'lucide-react'
import { Button } from '../ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { Skeleton } from '../ui/skeleton'
import UsageTracking from './UsageTracking'
import { AddDeviceModal } from './AddDeviceModal'
import { devicesQueryOptions, gatewayStatsQueryOptions } from '@/lib/queries'
import {
  channelStripeClass,
  getDeviceLabel,
  getDeviceState,
  isDeviceUp,
  stateDotClass,
} from '@/lib/devices'
import { cn } from '@/lib/utils'

export default function Overview() {
  const [addType, setAddType] = useState<'sms' | 'whatsapp' | null>(null)
  const [addOpen, setAddOpen] = useState(false)
  const openAdd = (type: 'sms' | 'whatsapp') => {
    setAddType(type)
    setAddOpen(true)
  }

  const { data: devices = [], isLoading: devicesLoading } = useQuery({
    ...devicesQueryOptions(),
    select: (data) => (Array.isArray(data) ? data : []),
  })
  const { data: stats } = useQuery(gatewayStatsQueryOptions())

  const sent = stats?.sentSMS ?? 0
  const delivered = stats?.deliveredSMS ?? 0
  const failed = stats?.failedSMS ?? 0
  const up = devices.filter(isDeviceUp).length

  const summary =
    devices.length === 0
      ? 'You have no senders yet.'
      : `${up} of ${devices.length} ${devices.length === 1 ? 'sender is' : 'senders are'} up. ` +
        `All time: ${sent.toLocaleString()} sent, ${delivered.toLocaleString()} delivered, ${failed.toLocaleString()} failed.`

  return (
    <div className="space-y-8">
      <p className="text-base text-ink num" aria-live="polite">
        {devicesLoading ? <Skeleton className="h-5 w-80 max-w-full" /> : summary}
      </p>

      <Card>
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 space-y-0">
          <CardTitle className="text-lg">Senders</CardTitle>
          <div className="flex gap-2">
            <Button asChild>
              <Link to="/dashboard/messages">Send a message</Link>
            </Button>
            <Button variant="outline" asChild>
              <Link to="/dashboard/messages?tab=bulk-send">Send to a list</Link>
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {devicesLoading ? (
            <Skeleton className="h-24 w-full" />
          ) : devices.length === 0 ? (
            <div className="py-6 space-y-4">
              <div>
                <h3 className="font-semibold text-ink">Connect your first sender</h3>
                <p className="text-sm text-gray-600 mt-1">
                  A sender is an Android phone (sends SMS) or a WhatsApp number. Messages go out from the one you pick.
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button onClick={() => openAdd('sms')}>
                  <Smartphone className="h-4 w-4 mr-2" />
                  Android phone (SMS)
                </Button>
                <Button variant="outline" onClick={() => openAdd('whatsapp')}>
                  <MessageCircle className="h-4 w-4 mr-2" />
                  WhatsApp number
                </Button>
              </div>
            </div>
          ) : (
            <ul className="divide-y divide-line">
              {devices.map((d: any) => {
                const { state, detail } = getDeviceState(d)
                return (
                  <li key={d.id}>
                    <Link
                      to={`/dashboard/devices/${d.id}`}
                      className="flex items-center gap-3 py-3 rounded-md hover:bg-secondary/60 px-2 -mx-2"
                    >
                      <span className={cn('h-9 w-[3px] rounded-full flex-shrink-0', channelStripeClass(d))} aria-hidden="true" />
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-ink truncate">
                          {getDeviceLabel(d)}
                          <span className="ml-2 text-xs font-normal text-gray-600">
                            {d.gatewayType === 'whatsapp' ? 'WhatsApp' : 'SMS'}
                          </span>
                        </p>
                        <p className="text-sm text-gray-600 flex items-center gap-1.5">
                          <span className={cn('h-2 w-2 rounded-full', stateDotClass[state])} aria-hidden="true" />
                          {detail}
                          {!d.enabled && ' · turned off'}
                        </p>
                      </div>
                      <span className="text-sm text-gray-600 num flex-shrink-0">
                        {(d.sentSMSCount ?? 0).toLocaleString()} sent
                      </span>
                    </Link>
                  </li>
                )
              })}
            </ul>
          )}
        </CardContent>
      </Card>

      <AddDeviceModal key={addType ?? 'choose'} open={addOpen} onOpenChange={setAddOpen} initial={addType} />

      <UsageTracking />
    </div>
  )
}
